// Api.
import api from '../Base';
import TagApi from '../TagApi';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import InstanceTargetHost from '../../types/instance/InstanceTargetHost';
import Tags from "../../types/Tags";
import { showError } from '../../utils/showAlert';

const InstanceApi = {

    getInstances:async () => {

        let url = `datasource?full=true`;

        let instances: InstanceTarget[] = [];

        await api.get<InstanceTarget[]>(url)
            .then((response) => {
                instances = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instances.', error);
                showError('Failed to retrieve instances', error.message || error.toString());
            });

        return instances;
    },

    getInstanceTypes:async () => {

        let url = `datasource/type?collect=true`;

        let instances: any[] = [];

        await api.get<any[]>(url)
            .then((response) => {
                instances = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instances.', error);
                showError('Failed to retrieve instances', error.message || error.toString());
            });

        return instances;
    },

    getInstanceStatisticTypes:async (isActivity: boolean) => {
        const metricType = isActivity ? 'activity' : 'statistic'
        let url = `${metricType}/type?collect=true`;

        let types: any[] = [];

        await api.get<any[]>(url)
            .then((response) => {
                types = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instances.', error);
                showError('Failed to retrieve instances', error.message || error.toString());
            });

        return types;
    },
    
    getInstance:(instanceId: number) => {

        let url = `datasource?full=true&id=${instanceId}`;

        api.get<InstanceTarget[]>(url)
            .then((response) => {
                return response.data[0];
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instance.', error);
                showError('Failed to retrieve instance', error.message || error.toString());
            });
    },

    getAvailableMetrics:async (url: string) => {
        let metrics: any = [];

        await api.get<[]>(url)
            .then((response) => {
                metrics = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve metric names.', error);
                showError('Failed to retrieve metric names', error.message || error.toString());
            });
        return metrics;
    },

    getTags: async (instanceId: number): Promise<Record<string, string>> => {
        const url = `datasource/tag?datasourceid=${instanceId}`;
        let tags: Record<string, string> = {};
        try {
            const response = await api.get<Tags[]>(url);
            if (response?.data) {
                // Transform the array of {tagname, tagvalue} objects into a Record<string, string>
                tags = response.data.reduce((acc, tag) => {
                    acc[tag.tagname] = tag.tagvalue; // Use correct property names
                    return acc;
                }, {} as Record<string, string>);
            }
        } catch (error: any) {
            console.error(`Failed to retrieve tags for instance ${instanceId}.`, error);
            showError(`Failed to retrieve tags for instance ${instanceId}`, error.message || error.toString());
            // Return empty object on error, or handle as needed
        }
        return tags;
    },

    getInstanceHostMapping: async (): Promise<InstanceTargetHost[]> => {
        return InstanceApi._getInstanceHost('');
    },

    getInstanceHostByInstance: async (instanceId: number): Promise<InstanceTargetHost[]> => {
        return InstanceApi._getInstanceHost(`id=${instanceId}`);
    },

    getInstanceHostByHost: async (hostId: number): Promise<InstanceTargetHost[]> => {
        return InstanceApi._getInstanceHost(`hostid=${hostId}`);
    },

    _getInstanceHost: async (queryParams: string): Promise<InstanceTargetHost[]> => {
        const url = `datasource/host${queryParams ? `?${queryParams}` : ''}`;
        let mapping: InstanceTargetHost[] = [];
        await api.get<InstanceTargetHost[]>(url)
            .then((response) => {
                mapping = response.data;
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instance-host mapping.', error);
                showError('Failed to retrieve instance-host mapping', error.message || error.toString());
            });
        return mapping;
    },

    getInstanceLinks: async (instanceId: number): Promise<any[]> => {
        const url = `datasource/link?datasourceid=${instanceId}`;
        let links: any[] = [];
        await api.get<any[]>(url)
            .then((response) => {
                links = response.data || [];
            })
            .catch((error: any) => {
                console.error('Failed to retrieve instance links.', error);
                showError('Failed to retrieve instance links', error.message || error.toString());
            });
        return links;
    },

    getAllTags: async (): Promise<Tags[]> => {
        return TagApi.getAllRawTags();
    },

    getInstancesWithTags: async (): Promise<InstanceTarget[]> => {
        try {
            // Fetch instances and tags in parallel
            const [instances, allTags] = await Promise.all([
                InstanceApi.getInstances(),
                TagApi.getAllRawTags()
            ]);

            // Group tags by datasourceid
            const tagsByInstanceId = allTags.reduce((acc, tag) => {
                if (tag.datasourceid !== undefined) {
                    if (!acc[tag.datasourceid]) {
                        acc[tag.datasourceid] = {};
                    }
                    acc[tag.datasourceid][tag.tagname] = tag.tagvalue;
                }
                return acc;
            }, {} as Record<number, Record<string, string>>);

            // Map tags to instances
            return instances.map(instance => ({
                ...instance,
                tags: tagsByInstanceId[instance.id] || {}
            }));
        } catch (error: any) {
            console.error('Failed to retrieve instances with tags.', error);
            showError('Failed to retrieve instances with tags', error.message || error.toString());
            // Fallback to instances without tags
            return InstanceApi.getInstances();
        }
    },

}

export default InstanceApi;
